/**
 * TemplateOnboardingAndLessonScreen
 * -----------------------------------------------------------------------------
 * FinTorro standard shell for onboarding + lessons.
 * - Fully locks to viewport (no scroll or white bleed)
 * - Centers content vertically
 * - Footer ends exactly at bottom of screen
 * © FinTorro
 */
/**
 * TemplateOnboardingAndLessonScreen.jsx
 * -----------------------------------------------------------------------------
 * FinTorro universal template shell for onboarding + lessons.
 * Mirrors ActivationIntroSequence pattern:
 *  - Uses <LottieSmart /> for animations
 *  - Fixed non-scrollable viewport
 *  - Continue triggers banner + Lottie overlay
 * © FinTorro
 */
/**
 * TemplateOnboardingAndLessonScreen.jsx
 * -----------------------------------------------------------------------------
 * FinTorro universal template shell for onboarding + lessons.
 * Mirrors ActivationIntroSequence pattern:
 *  - Uses <LottieSmart /> for animations
 *  - Fixed non-scrollable viewport
 *  - Continue triggers sparkle/confetti + slide-up banner
 * © FinTorro
 */

import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import LottieSmart from "../components/animation/LottieSmart.jsx";

// -----------------------------------------------------------------------------
// ANIMATIONS (same structure as ActivationIntroSequence)
// -----------------------------------------------------------------------------
const ANIMS = {
  confetti: {
    src: "/assets/animations/confetti.json",
    fallbackSrc:
      "https://lottie.host/a8020b46-6c8b-401f-a9aa-524c4ab03032/eXsEvELgZA.json",
    w: 400,
    h: 400,
    loop: false,
    autoplay: true,
  },
  coinsparkle: {
    src: "/assets/animations/coin_sparkle.json",
    fallbackSrc:
      "https://lottie.host/17f89781-7c01-4038-b8eb-34f05839ad8e/I3He0k0iNH.json",
    w: 180,
    h: 180,
    loop: true,
    autoplay: true,
  },
  xpfill: {
    src: "/assets/animations/xp_bar_fill.json",
    fallbackSrc: "",
    w: 180,
    h: 180,
    loop: true,
    autoplay: true,
  },
  flagplant: {
    src: "/assets/animations/flag_plant.json",
    fallbackSrc: "",
    w: 180,
    h: 180,
    loop: false,
    autoplay: true,
  },
};

// -----------------------------------------------------------------------------
// COLORS
// -----------------------------------------------------------------------------
const COLORS = {
  orange: "#FF7C24",
  orangeAccent: "#FFB800",
  bgDark: "#0B0D11",
  textLight: "#FFFFFF",
  textDim: "#DDE2EA",
  border: "rgba(255,255,255,0.08)",
};

// -----------------------------------------------------------------------------
// COMPONENT
// -----------------------------------------------------------------------------
export default function TemplateOnboardingAndLessonScreen({
  title,
  progress = 0,
  lives = 3,
  canContinue = false,
  animationType = "confetti",
  onContinue,
  onSkip,
  onClose,
  children,
}) {
  const [showBanner, setShowBanner] = useState(false);

  // Lock scrolling
  useEffect(() => {
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, []);

  const anim = ANIMS[animationType] || ANIMS.confetti;

  const handleContinueClick = () => {
    // When animationType is confetti, use confetti burst overlay instead
    setShowBanner(animationType === "confetti" ? "confetti" : "sparkle");
    setTimeout(() => {
      setShowBanner(false);
      onContinue && onContinue();
    }, 2400);
  };

  return (
    <div style={S.page}>
      {/* Animation CSS for banner pop */}
      <style>
        {`
          @keyframes ftBannerPop {
            0% { transform: translateY(20px); opacity: 0; }
            20% { transform: translateY(0); opacity: 1; }
            80% { transform: translateY(-10px); opacity: 1; }
            100% { transform: translateY(-30px); opacity: 0; }
          }
          .ft-banner-pop {
            animation: ftBannerPop 2.4s ease-in-out forwards;
          }
        `}
      </style>

      {/* Header */}
      <header style={S.topbar}>
        <div style={S.headerInner}>
          <button style={S.close} aria-label="Close" onClick={onClose}>
            ✕
          </button>
          <div style={S.progress}>
            <span style={{ ...S.progressFill, width: `${progress}%` }} />
          </div>
          <div style={S.lives}>
            <span style={S.heart} />
            <span style={S.livesText}>{lives}</span>
          </div>
        </div>
      </header>

      {/* Stage */}
      <main style={S.stage}>
        <div style={S.centerBlock}>
          <h1 style={S.title}>{title}</h1>

          {/* Core Content */}
          <div style={S.content}>
            {children}

            {/* Base animation (e.g., looping sparkle or flagplant) */}
            <LottieSmart
              src={anim.src}
              fallbackSrc={anim.fallbackSrc}
              loop={anim.loop}
              autoplay={anim.autoplay}
              style={{ width: anim.w, height: anim.h }}
            />
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer style={S.footer}>
        <hr style={S.separator} />
        <div style={S.actions}>
          <button style={{ ...S.btn, ...S.btnGhost }} onClick={onSkip}>
            Skip
          </button>
          <button
            style={{
              ...S.btn,
              ...S.btnPrimary,
              opacity: canContinue ? 1 : 0.5,
              cursor: canContinue ? "pointer" : "not-allowed",
            }}
            disabled={!canContinue}
            onClick={handleContinueClick}
          >
            Continue
          </button>
        </div>
      </footer>

      {/* Celebration Overlay */}
   {/* Celebration Overlay */}
{showBanner && (
  <div style={S.bannerWrap}>
    {/* Full-screen Lottie animation */}
    <div style={S.lottieWrap}>
    <div style={S.overlay} />
      <LottieSmart
        key={Date.now()}
        src={
          showBanner === "confetti"
            ? ANIMS.confetti.src
            : ANIMS.coinsparkle.src
        }
        fallbackSrc={
          showBanner === "confetti"
            ? ANIMS.confetti.fallbackSrc
            : ANIMS.coinsparkle.fallbackSrc
        }
        loop={false}
        autoplay={true}
        style={{
          width: "100vw",
          height: "100vh",
          opacity: 0.85,
        }}
      />
    </div>

    {/* Pop-up banner */}
    <div style={S.banner} className="ft-banner-pop">
      {showBanner === "confetti"
        ? "🎊 You did it! Lesson complete!"
        : "🎉 Continue button pressed!"}
    </div>
  </div>
)}
    </div>
  );
}

TemplateOnboardingAndLessonScreen.propTypes = {
  title: PropTypes.string,
  progress: PropTypes.number,
  lives: PropTypes.number,
  canContinue: PropTypes.bool,
  animationType: PropTypes.string,
  onContinue: PropTypes.func,
  onSkip: PropTypes.func,
  onClose: PropTypes.func,
  children: PropTypes.node,
};

// -----------------------------------------------------------------------------
// STYLES
// -----------------------------------------------------------------------------
const S = {
  page: {
    background: COLORS.bgDark,
    height: "100vh",
    width: "100vw",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    color: COLORS.textDim,
    fontFamily: "Nunito Sans, sans-serif",
    position: "relative",
  },
  topbar: {
    display: "flex",
    justifyContent: "center",
    padding: "12px 0 0",
  },
  headerInner: {
    width: "min(980px, 96vw)",
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  close: {
    width: 38,
    height: 38,
    borderRadius: 12,
    display: "grid",
    placeItems: "center",
    border: `1px solid ${COLORS.border}`,
    background: "rgba(255,255,255,0.02)",
    color: COLORS.textLight,
    fontWeight: "bold",
    cursor: "pointer",
  },
  progress: {
    flex: 1,
    height: 10,
    background: COLORS.border,
    borderRadius: 999,
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    background: `linear-gradient(90deg, ${COLORS.orange}, ${COLORS.orangeAccent})`,
    borderRadius: 999,
    transition: "width 0.3s ease",
  },
  lives: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontWeight: 800,
  },
  heart: {
    width: 18,
    height: 18,
    borderRadius: 4,
    background: "#FF4C4C",
    transform: "rotate(45deg)",
  },
  livesText: { fontSize: 14, fontWeight: 800 },
  stage: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
  centerBlock: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: 800,
    color: COLORS.textLight,
  },
  content: {
    width: 280,
    height: 220,
    display: "grid",
    placeItems: "center",
    background: "rgba(255,255,255,0.03)",
    border: `1px dashed ${COLORS.border}`,
    borderRadius: 16,
    position: "relative",
    overflow: "hidden",
  },
  footer: {
    padding: "0 16px 20px",
  },
  separator: {
    border: "none",
    height: 3,
    background: COLORS.textLight,
    borderRadius: 4,
    margin: "0 0 12px",
  },
  actions: {
    width: "min(980px, 96vw)",
    margin: "0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  btn: {
    fontSize: 16,
    fontWeight: 800,
    padding: "14px 22px",
    borderRadius: 16,
    border: "2px solid transparent",
  },
  btnGhost: {
    background: "transparent",
    borderColor: "rgba(255,255,255,.14)",
    color: "rgba(255,255,255,.88)",
  },
  btnPrimary: {
    background: COLORS.orange,
    color: "#1a110a",
  },
  bannerWrap: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    zIndex: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
  },
  lottieWrap: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    zIndex: 5,
  },
  banner: {
    position: "relative",
    zIndex: 20,
    background: COLORS.orange,
    color: "#1a110a",
    fontWeight: 800,
    padding: "12px 24px",
    borderRadius: 12,
    fontSize: 16,
    boxShadow: "0 6px 20px rgba(255,124,36,0.3)",
  },
};